import { ArrowLeft, ListPlus, Package, Plus } from 'lucide-react';
import { useState } from 'react';
import type {
  Draft,
  DraftLine,
  InvoiceKind,
  Item,
  OrganizationTax,
  PreviewResult,
} from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { Button } from '../../../ui/Button';
import { DecimalField } from '../../../ui/DecimalField';
import { Sheet } from '../../../ui/Sheet';
import { ItemForm } from '../../items/ItemForm';
import { ItemPickerSheet } from '../../items/ItemPickerSheet';
import styles from '../Sales.module.css';
import { LineEditor, type LineInfo, type NoteLineDisplay } from './LineEditor';
import { useNoteReferences } from './noteReferences';
import { ReferenceLinesSheet, type ReferenceLineOption } from './ReferenceLinesSheet';
import type { EditorMode } from './steps';

/**
 * Step 3, «بنود الفاتورة» (BLOCK-1-REDESIGN §A.2, §A.4): on an invoice, «الاختيار من الكتالوج» opens
 * the catalogue side sheet (the lines stay visible beside it), the line cards, and the
 * document-level discount under the lines. «تعديل الصنف» on a line opens the item form in its own
 * side sheet (M-05).
 *
 * Block 11 note mode (notes-editor.md §3): there is NO catalogue picker and NO new-item form at any
 * state (AC-N1). Lines come only through the reference-lines sheet (`ReferenceLinesSheet`, AC-N8),
 * each referenced line at most once; the item, unit and taxes render as text and only quantity and
 * price are editable (AC-N5). Before a reference is chosen the step reads «اختر المستند المرجعي
 * أولًا» with «عُد إلى المستند المرجعي».
 */
export function LinesStep({
  orgId,
  draft,
  kind,
  mode = 'invoice',
  items,
  result,
  taxes,
  issueOf,
  canEditItem,
  onAdd,
  onAddReferenceLines,
  onBackToReference,
  onLine,
  onPickItem,
  onRemove,
  onItemSaved,
  onExtraDiscount,
}: {
  orgId: string;
  draft: Draft;
  kind: InvoiceKind;
  /** Block 11: a note's lines come from the reference, not the catalogue (D-2, AC-N1, AC-N8). */
  mode?: EditorMode;
  items: Record<string, LineInfo>;
  result: PreviewResult | undefined;
  taxes: OrganizationTax[];
  issueOf: (path: string) => string | undefined;
  canEditItem: boolean;
  /** Invoice mode: adds one line per picked item (defaults copied; no automatic tax on export lines). */
  onAdd: (items: Item[]) => void;
  /** Note mode: adds one line per picked reference line (quantity and price default to the reference's). */
  onAddReferenceLines?: (picked: ReferenceLineOption[]) => void;
  /** Note mode: «عُد إلى المستند المرجعي» — back to step 1. */
  onBackToReference?: () => void;
  onLine: (index: number, patch: Partial<DraftLine>) => void;
  onPickItem: (index: number, item: Item) => void;
  onRemove: (index: number) => void;
  /** The item of a line was edited in the catalogue: the line follows it. */
  onItemSaved: (index: number, itemId: string) => void;
  onExtraDiscount: (value: string) => void;
}) {
  const { t, pick } = useI18n();
  const isNote = mode === 'note';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState<{ line: number; itemId: string } | null>(null);
  const [added, setAdded] = useState('');

  // Note mode: the reference lines (and the invoice numbers) that step 3 shows and the sheet offers.
  const references = isNote ? (draft.references ?? []) : [];
  // A referenced line's facts, by its id, with the number of the invoice it came from.
  const { refLines } = useNoteReferences(orgId, kind, references);
  const taken = new Set(
    draft.lines.flatMap((l) => (l.referencedLineId ? [l.referencedLineId] : [])),
  );
  const available: ReferenceLineOption[] = [...refLines.values()].flatMap(
    ({ invoiceNumber, line }) => (taken.has(line.id) ? [] : [{ invoiceNumber, line }]),
  );
  const noteLineOf = (line: DraftLine): NoteLineDisplay | undefined => {
    if (!isNote || !line.referencedLineId) return undefined;
    const ref = refLines.get(line.referencedLineId);
    if (!ref) return undefined;
    return {
      invoiceNumber: ref.invoiceNumber,
      lineNo: ref.line.lineNo,
      description: ref.line.description,
      code: `${ref.line.itemType} ${ref.line.itemCode}`,
      unit: ref.line.unitType,
      weightUnit: ref.line.weightUnitType,
      taxes: ref.line.taxes.map((tx) => ({
        taxType: tx.taxType,
        subType: tx.subType,
        organizationTaxId: tx.organizationTaxId,
        kind: tx.kind,
      })),
    };
  };

  // Note mode, before a reference is chosen: nothing to add, so no catalogue and no empty-line form.
  if (isNote && references.length === 0) {
    return (
      <div className={styles.stepBody}>
        <div className={styles.emptyLines} data-field="references">
          <Package size={26} aria-hidden="true" />
          <span>{t.editor.note.noReference}</span>
          <Button
            variant="secondary"
            icon={<ArrowLeft size={16} className="flip-rtl" aria-hidden="true" />}
            onPress={() => onBackToReference?.()}
            testId="back-to-reference"
          >
            {t.editor.note.backToReference}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepBody}>
      <div className={styles.linesHead}>
        {isNote ? null : <p className={styles.note}>{t.editor.lines.addItemsHint}</p>}
        <Button
          variant="primary"
          icon={<ListPlus size={16} aria-hidden="true" />}
          onPress={() => setPickerOpen(true)}
          testId="add-items"
        >
          {isNote ? t.editor.note.addLine : t.editor.lines.addItems}
        </Button>
      </div>
      {draft.lines.length === 0 ? (
        <div className={styles.emptyLines} data-field="add-items">
          <Package size={26} aria-hidden="true" />
          {isNote ? null : <span>{t.editor.lines.empty}</span>}
          <Button
            variant="secondary"
            icon={isNote ? <ListPlus size={16} /> : <Plus size={16} />}
            onPress={() => setPickerOpen(true)}
          >
            {isNote ? t.editor.note.addLine : t.editor.lines.addItems}
          </Button>
        </div>
      ) : (
        <div className={styles.lineCards}>
          {draft.lines.map((line, i) => (
            <LineEditor
              key={i}
              orgId={orgId}
              index={i}
              line={line}
              kind={kind}
              currency={draft.currency}
              info={line.itemId ? items[line.itemId] : undefined}
              computed={result?.lines[i]}
              taxes={taxes}
              issueOf={issueOf}
              canEditItem={canEditItem}
              note={noteLineOf(line)}
              onChange={(patch) => onLine(i, patch)}
              onPickItem={(item) => onPickItem(i, item)}
              onRemove={() => onRemove(i)}
              onEditItem={() => line.itemId && setEditing({ line: i, itemId: line.itemId })}
              pick={pick}
            />
          ))}
        </div>
      )}
      <div className={styles.extraDiscount}>
        <DecimalField
          label={t.editor.totals.extraDiscount}
          fieldName="extraDiscountAmount"
          value={draft.extraDiscountAmount ?? ''}
          onChange={onExtraDiscount}
          isOptional
          errorMessage={issueOf('extraDiscountAmount')}
        />
        <p className={styles.note}>{t.editor.totals.egpNote}</p>
      </div>
      <p className="visually-hidden" role="status" aria-live="polite">
        {added}
      </p>
      {isNote ? (
        <ReferenceLinesSheet
          isOpen={pickerOpen}
          onOpenChange={setPickerOpen}
          options={available}
          currency={draft.currency}
          onConfirm={(picked) => {
            onAddReferenceLines?.(picked);
            setAdded(t.catalogue.linesAdded(picked.length));
          }}
        />
      ) : (
        <>
          <ItemPickerSheet
            orgId={orgId}
            isOpen={pickerOpen}
            onOpenChange={setPickerOpen}
            onConfirm={(picked) => {
              onAdd(picked);
              setAdded(t.catalogue.linesAdded(picked.length));
            }}
          />
          <Sheet
            isOpen={editing !== null}
            onOpenChange={(open) => {
              if (!open) setEditing(null);
            }}
            title={t.catalogue.editTitle}
          >
            {editing ? (
              <ItemForm
                orgId={orgId}
                itemId={editing.itemId}
                onCancel={() => setEditing(null)}
                onSaved={({ id }) => {
                  onItemSaved(editing.line, id);
                  setEditing(null);
                }}
              />
            ) : null}
          </Sheet>
        </>
      )}
    </div>
  );
}
